// Cách viết số của thẻ «06 Tiểu đội» (bản mẫu 1a): tiền gọn kiểu tạp chí ("1,28 tỷ", "900 tr"),
// phần trăm đạt mục tiêu từ phần nghìn máy chủ trả (1234 = 123,4%).

import type { components } from "@/lib/api/generated/schema";

const so = (v: number, digits: number) =>
  v.toLocaleString("vi-VN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** 1_280_000_000 → "1,28 tỷ"; 900_000_000 → "900 tr"; 0 → "0". */
export function tienGon(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${so(n / 1_000_000_000, 2)} tỷ`;
  if (abs >= 1_000_000) return `${so(Math.round(n / 1_000_000), 0)} tr`;
  if (abs >= 1_000) return `${so(Math.round(n / 1_000), 0)}k`;
  return so(n, 0);
}

/** Phần nghìn → "117%" hoặc "106,5%"; null (chưa có mục tiêu) → "". */
export function phanTram(perMille: number | null | undefined): string {
  if (perMille == null) return "";
  return `${so(perMille / 10, perMille % 10 === 0 ? 0 : 1)}%`;
}

/** Hiệu hai mức phần nghìn → "11 điểm %" / "3,5 điểm %". */
export function diemPhanTram(diffPerMille: number): string {
  const d = Math.max(0, diffPerMille);
  return `${so(d / 10, d % 10 === 0 ? 0 : 1)} điểm %`;
}

/** Bề rộng vạch tiến độ (0–100) theo % đạt; vượt 100% thì đầy vạch. */
export function vach(perMille: number | null | undefined): number {
  if (perMille == null) return 0;
  return Math.max(0, Math.min(100, perMille / 10));
}

/** "2026-11" → "Tháng 11/2026". */
export function nhanThang(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  return m ? `Tháng ${m[2]}/${m[1]}` : month;
}

/** Số ngày lịch còn lại tới hết tháng (tính cả hôm nay là 0 khi hôm nay là ngày cuối). */
export function ngayConLai(month: string, now: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return null;
  const last = new Date(Number(m[1]), Number(m[2]), 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((last.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Dòng phụ dưới tên tiểu đội: «TĐT A · Đội 1 · ĐT B» (viết tắt tiểu đội trưởng, đội trưởng). Chức còn
 * trống thì bỏ hẳn, không ghi «chưa có…» (CEO 02/11/2026); không còn gì để ghi ⇒ chuỗi rỗng.
 */
export function squadLeadLine(badge: components["schemas"]["SalesSquadBadge"]): string {
  const parts: string[] = [];
  if (badge.leadName) parts.push(`TĐT ${badge.leadName}`);
  if (badge.teamName) {
    parts.push(badge.teamName);
    if (badge.teamLeadName) parts.push(`ĐT ${badge.teamLeadName}`);
  }
  return parts.join(" · ");
}

/**
 * Hai cách xếp của thẻ «06 Tiểu đội» (CEO 01/11/2026): «% đạt mục tiêu» (thứ tự máy chủ trả — giữ
 * ngôi, khung đội đọc theo nó) hoặc «Doanh số» (doanh thu thật, máy chủ đánh sẵn `revenueRank`).
 */
export type RankBy = "percent" | "revenue";

type Ranked = { rank: number; revenueRank: number };

/** Hàng theo cách xếp đang chọn — bản sao, không đổi mảng gốc. */
export function theoCachXep<T extends Ranked>(rows: readonly T[], by: RankBy): T[] {
  if (by === "percent") return [...rows];
  return [...rows].sort((a, b) => a.revenueRank - b.revenueRank);
}

/** Bề rộng vạch doanh số (0–100) so với tiểu đội / miền dẫn đầu; âm hoặc chưa ai có doanh số ⇒ 0. */
export function vachTien(v: number, max: number): number {
  if (max <= 0 || v <= 0) return 0;
  return Math.min(100, (v / max) * 100);
}
